/**
 * Sherlock Holmes — „Unmatched: Cobble & Fog".
 *
 * Efekty kariet HOLMES a DR. WATSON súper zrušiť nemôže — karty ANY áno
 * (`cardsUncancelable` s ID karty). Elementary tipuje hodnotu útoku ešte pred
 * odhalením (`CardEffect.beforeReveal`, súboj počká v kroku `predict`).
 */

import { areAdjacent, neighbors } from '../map.js';
import type { Ctx } from '../ops.js';
import { askChoice, cardDef, dealDamage, discardFromHand, drawCard, emit, getMap, getPlayer, healFighter, heroOf, swapFighters } from '../ops.js';
import type { PlayerIndex } from '../types.js';
import { feint } from './common.js';
import { askMove, cancel, fighterOrNull, opponentOf, revealHand } from './helpers.js';
import type { CardEffect, EffectCtx, EffectRegistry, HeroSpecial } from './types.js';

const HOLMES = 'sherlock-holmes';
const WATSON = 'dr-watson';
const DEDUCE = 'sherlock-holmes.deduce-strategy';
const ELEMENTARY = 'sherlock-holmes.elementary';
const SUSPICION = 'sherlock-holmes.confirm-suspicion';
/** Tipovateľné vytlačené hodnoty kariet. */
const VALUES = [0, 1, 2, 3, 4, 5, 6, 7, 8];

const figure = (ctx: Ctx, player: PlayerIndex, defId: string) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === defId) ?? null;

/** „If you won the combat, your opponent draws 1 card. If you lost the combat, you draw 2 cards." */
const educationNeverEnds: CardEffect = {
  after: (e) => {
    const opponent = opponentOf(e);
    if (e.won) {
      if (opponent !== null) drawCard(e.ctx, opponent);
      return;
    }
    drawCard(e.ctx, e.player);
    drawCard(e.ctx, e.player);
  },
};

/** „If you won the combat, look at your opponent's hand." */
const studyMethods: CardEffect = {
  after: (e) => {
    const opponent = opponentOf(e);
    if (e.won && opponent !== null) revealHand(e, opponent);
  },
};

/**
 * „DURING: You may change the printed value of the opponent's card to its
 * BOOST value." Príznak `boostValue:<strana>` (ako This Is No Parlor Trick).
 */
const deduceStrategy: CardEffect = {
  duringChoice: (e) => {
    const theirs = e.role === 'defender' ? 'attacker' : 'defender';
    const combat = e.ctx.state.combat;
    const theirCard = theirs === 'attacker' ? combat?.attackCard : combat?.defenseCard;
    if (!combat || !theirCard || (theirs === 'attacker' && combat.bonus)) return;
    const def = cardDef(e.ctx, theirCard);
    askChoice(e.ctx, {
      kind: 'option',
      player: e.player,
      source: e.cardId,
      label: `Deduce Strategy: zmeniť vytlačenú hodnotu ${def.name} (${def.value}) na jej BOOST (${def.boost})?`,
      optional: true,
      options: [{ label: `Áno — hodnota ${def.boost}`, follow: { kind: 'effect', effect: DEDUCE, data: theirs } }],
    });
  },
  onFollow: (ctx, _choice, side) => {
    const combat = ctx.state.combat;
    if (combat) (combat.flags ??= []).push(`boostValue:${side}`);
  },
};

/** „If Holmes is adjacent to the opposing fighter, deal 2 damage to that fighter." */
const counterpunch: CardEffect = {
  after: (e) => {
    const holmes = figure(e.ctx, e.player, HOLMES);
    const them = fighterOrNull(e.ctx, e.opponentFighterId);
    if (holmes && them && areAdjacent(getMap(e.ctx), holmes.spaceId, them.spaceId)) dealDamage(e.ctx, them.id, 2);
  },
};

/** „If Dr. Watson is adjacent to Holmes, they each recover 1 health." */
const fixedPoint: CardEffect = {
  after: (e) => {
    const holmes = figure(e.ctx, e.player, HOLMES);
    const watson = figure(e.ctx, e.player, WATSON);
    if (!holmes || !watson || !areAdjacent(getMap(e.ctx), holmes.spaceId, watson.spaceId)) return;
    healFighter(e.ctx, holmes.id, 1);
    healFighter(e.ctx, watson.id, 1);
  },
};

/** „Move Holmes up to 3 spaces." */
const gameIsAfoot: CardEffect = {
  after: (e) => {
    const holmes = figure(e.ctx, e.player, HOLMES);
    if (holmes) askMove(e, { fighters: [holmes.id], max: 3, label: 'The Game Is Afoot: presuň Holmesa až o 3' });
  },
};

/**
 * „Play this card face up. Predict the printed attack value of the opponent's
 * card. DURING: If you predicted the correct value, cancel all effects on your
 * opponent's card and ignore its attack value."
 */
const elementary: CardEffect = {
  beforeReveal: (e) => {
    askChoice(e.ctx, {
      kind: 'option',
      player: e.player,
      source: e.cardId,
      label: 'Elementary: tipni vytlačenú hodnotu súperovho útoku',
      optional: false,
      options: VALUES.map((value) => ({
        label: String(value),
        follow: { kind: 'effect' as const, effect: ELEMENTARY, data: String(value) },
      })),
    });
  },
  onFollow: (ctx, choice, value) => {
    const combat = ctx.state.combat;
    if (!combat) return;
    combat.prediction = Number(value);
    emit(ctx, { type: 'specialUsed', player: choice.player, heroId: HOLMES, name: `Elementary: tip ${value}` });
  },
  duringChoice: (e) => {
    const combat = e.ctx.state.combat;
    if (!combat?.attackCard || combat.prediction === undefined) return;
    const printed = combat.bonus ? combat.bonus.value : cardDef(e.ctx, combat.attackCard).value;
    if (printed !== combat.prediction) return;
    emit(e.ctx, { type: 'specialUsed', player: e.player, heroId: HOLMES, name: 'Elementary: tip vyšiel!' });
    cancel(e.ctx, 'attacker');
    (combat.flags ??= []).push('zeroAttack');
  },
};

/**
 * „Choose an opponent and name a value. Your opponent must choose and discard
 * one card matching that attack or defense value. Their hero takes damage
 * equal to the BOOST value of the discarded card. If they do not have a card
 * of the named value, they must reveal their hand instead."
 */
const confirmSuspicion: CardEffect = {
  scheme: (e) => {
    const opponent = opponentOf(e);
    if (opponent === null) return;
    askChoice(e.ctx, {
      kind: 'option',
      player: e.player,
      source: e.cardId,
      label: 'Confirm Suspicion: vyslov hodnotu',
      optional: false,
      options: VALUES.map((value) => ({
        label: String(value),
        follow: { kind: 'effect' as const, effect: SUSPICION, data: `${opponent}:${value}` },
      })),
    });
  },
  onFollow: (ctx, choice, data) => {
    const [opponent, value] = data.split(':').map(Number) as [number, number];
    const hand = getPlayer(ctx.state, opponent).hand;
    // Schémy útočnú ani obrannú hodnotu nemajú.
    const matching = hand.filter((id) => {
      const def = cardDef(ctx, id);
      return def.type !== 'scheme' && def.value === value;
    });
    emit(ctx, { type: 'specialUsed', player: choice.player, heroId: HOLMES, name: `Confirm Suspicion: hodnota ${value}` });
    if (matching.length === 0) {
      const e = { ctx, cardId: choice.source ?? SUSPICION, player: choice.player } as EffectCtx;
      revealHand(e, opponent);
      return;
    }
    askChoice(ctx, {
      kind: 'card',
      player: opponent,
      source: choice.source,
      label: `Confirm Suspicion: odhoď kartu s hodnotou ${value} — jej BOOST dostane tvoj hrdina`,
      optional: false,
      from: 'hand',
      purpose: 'effect',
      only: matching,
      discards: true,
    });
  },
  onCardChosen: (ctx, choice, cardId) => {
    const boost = cardDef(ctx, cardId).boost;
    discardFromHand(ctx, choice.player, cardId, 'effect', true);
    const hero = heroOf(ctx.state, choice.player);
    if (hero) dealDamage(ctx, hero.id, boost);
  },
};

/** „Choose an opponent. Look at their hand and choose 1 card for them to discard." */
const eliminateTheImpossible: CardEffect = {
  scheme: (e) => {
    const opponent = opponentOf(e);
    if (opponent === null || getPlayer(e.ctx.state, opponent).hand.length === 0) return;
    if (!revealHand(e, opponent)) return;
    askChoice(e.ctx, {
      kind: 'card',
      player: e.player,
      source: e.cardId,
      label: 'Eliminate the Impossible: vyber kartu, ktorú súper odhodí',
      optional: false,
      from: 'hand',
      owner: opponent,
      purpose: 'discard',
    });
  },
};

/** „Choose an opponent. Holmes swaps spaces with their hero. Deal 1 damage to that hero." */
const masterOfDisguise: CardEffect = {
  scheme: (e) => {
    const opponent = opponentOf(e);
    const holmes = figure(e.ctx, e.player, HOLMES);
    const hero = opponent === null ? undefined : heroOf(e.ctx.state, opponent);
    if (!holmes || !hero) return;
    swapFighters(e.ctx, holmes.id, hero.id);
    dealDamage(e.ctx, hero.id, 1);
  },
};

/** „Place Dr. Watson in a space adjacent to Holmes. Holmes recovers 1 health. Draw 1 card." */
const administerAid: CardEffect = {
  scheme: (e) => {
    const holmes = figure(e.ctx, e.player, HOLMES);
    const watson = figure(e.ctx, e.player, WATSON);
    if (holmes && watson) {
      const occupied = new Set(e.ctx.state.fighters.map((f) => f.spaceId));
      const spaces = neighbors(getMap(e.ctx), holmes.spaceId).filter((id) => !occupied.has(id));
      if (spaces.length > 0) {
        askChoice(e.ctx, {
          kind: 'place',
          player: e.player,
          source: e.cardId,
          label: 'Administer Aid: polož Dr. Watsona vedľa Holmesa',
          optional: false,
          fighterId: watson.id,
          spaces,
        });
      }
    }
    if (holmes) healFighter(e.ctx, holmes.id, 1);
    drawCard(e.ctx, e.player);
  },
};

export const sherlockCards: EffectRegistry = {
  'sherlock-holmes.feint': feint,
  'sherlock-holmes.education-never-ends': educationNeverEnds,
  'sherlock-holmes.study-methods': studyMethods,
  [DEDUCE]: deduceStrategy,
  'sherlock-holmes.counterpunch': counterpunch,
  'sherlock-holmes.fixed-point-in-a-changing-age': fixedPoint,
  'sherlock-holmes.the-game-is-afoot': gameIsAfoot,
  [ELEMENTARY]: elementary,
  [SUSPICION]: confirmSuspicion,
  'sherlock-holmes.eliminate-the-impossible': eliminateTheImpossible,
  'sherlock-holmes.master-of-disguise': masterOfDisguise,
  'sherlock-holmes.administer-aid': administerAid,
  // Service Revolver: bez efektu.
};

export const sherlockSpecial: HeroSpecial = {
  name: 'Holmes & Watson',
  /** „Effects on HOLMES and DR. WATSON cards cannot be cancelled by an opponent. (ANY cards can.)" */
  cardsUncancelable: (ctx, _player, cardId) => {
    const character = ctx.state.cards[cardId] ? cardDef(ctx, cardId).character : null;
    return character === HOLMES || character === WATSON;
  },
};
